import { useState } from "react";
import Router from "@/constants/router";
import type { LoginRequestDto } from "@/dto/AdmDto";
import { useAuthStore } from "@/store";
import { AxiosError } from "axios";

export default function useLoginPage() {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // 🔥 zustand authStore 사용
  const { login: authLogin } = useAuthStore();

  const handleLogin = async (data: LoginRequestDto) => {
    try {
      setIsLoading(true);
      setError(false);
      setErrorMessage("");
      console.log("🔐 슈퍼어드민 로그인 페이지 시도:", {
        admId: data.admId,
        pwdLength: data.pwd?.length || 0,
      });

      // 🔥 authStore를 통한 로그인
      const result = await authLogin({
        admId: data.admId,
        pwd: data.pwd,
      });

      console.log("🔍 슈퍼어드민 로그인 결과:", result);

      if (result.success) {
        console.log("✅ 슈퍼어드민 로그인 성공 - 대시보드로 이동");
        // 🔥 navigate 대신 window.location을 사용하여 완전한 페이지 새로고침
        window.location.href = Router.Dashboard;
      } else {
        console.error("❌ 슈퍼어드민 로그인 실패:", result.error);
        setError(true);
        setErrorMessage(result.error || "로그인에 실패했습니다.");
      }
    } catch (e: unknown) {
      console.error("❌ 슈퍼어드민 로그인 페이지 에러:", e);
      console.log("🔍 슈퍼어드민 에러 객체 타입:", typeof e);
      console.log("🔍 슈퍼어드민 에러 객체 전체:", JSON.stringify(e, null, 2));

      let finalErrorMessage =
        "로그인 중 오류가 발생했습니다. 다시 시도해주세요.";

      // 🔥 TypeScript 안전한 에러 처리
      if (e && typeof e === "object") {
        // AxiosError 패턴 확인
        if (e instanceof AxiosError) {
          console.log("📡 슈퍼어드민 AxiosError 감지");

          const responseData = e.response?.data as
            | Record<string, unknown>
            | undefined;

          if (
            responseData?.details &&
            typeof responseData.details === "object"
          ) {
            const details = responseData.details as Record<string, unknown>;
            if (typeof details.errorMessage === "string") {
              finalErrorMessage = details.errorMessage;
              console.log("✅ details.errorMessage 사용:", finalErrorMessage);
            }
          } else if (typeof responseData?.message === "string") {
            finalErrorMessage = responseData.message;
            console.log("✅ responseData.message 사용:", finalErrorMessage);
          } else if (typeof responseData?.error === "string") {
            finalErrorMessage = responseData.error;
            console.log("✅ responseData.error 사용:", finalErrorMessage);
          } else if (typeof e.message === "string") {
            finalErrorMessage = e.message;
            console.log("✅ e.message 사용:", finalErrorMessage);
          }
        } else {
          // 일반 객체 에러 처리
          const errorObj = e as Record<string, unknown>;

          if (typeof errorObj.message === "string") {
            finalErrorMessage = errorObj.message;
            console.log("✅ errorObj.message 사용:", finalErrorMessage);
          } else if (typeof errorObj.error === "string") {
            finalErrorMessage = errorObj.error;
            console.log("✅ errorObj.error 사용:", finalErrorMessage);
          }
        }
      } else if (typeof e === "string") {
        finalErrorMessage = e;
        console.log("✅ 문자열 에러 사용:", finalErrorMessage);
      }

      console.log("🔍 슈퍼어드민 최종 에러 메시지:", finalErrorMessage);

      setError(true);
      setErrorMessage(finalErrorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    errorMessage,
    handleLogin,
  };
}
